import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, X } from 'lucide-react';

const GalleryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Real RC Awards shop images
  const galleryImages = [
    {
      id: 1,
      src: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      alt: 'RC Awards Shop Interior - Full showroom view',
      category: 'showroom',
      title: 'Our Showroom'
    },
    {
      id: 2,
      src: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/oumok1jw_unnamed%20%286%29.webp',
      alt: 'Custom trophies display at RC Awards',
      category: 'trophies',
      title: 'Trophy Collection'
    },
    {
      id: 3,
      src: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/ehppwmya_unnamed%20%287%29.webp',
      alt: 'Crystal and art glass awards',
      category: 'crystal',
      title: 'Crystal & Art Glass Awards'
    },
    {
      id: 4,
      src: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp',
      alt: 'Sports trophies wall display',
      category: 'trophies',
      title: 'Sports Trophies'
    },
    {
      id: 5,
      src: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/8lcfsx9e_unnamed%20%289%29.webp',
      alt: 'RC Awards storefront exterior',
      category: 'showroom',
      title: 'Our Storefront'
    }
  ];

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'showroom', label: 'Showroom' },
    { id: 'trophies', label: 'Trophies' },
    { id: 'crystal', label: 'Crystal Awards' }
  ];

  const filteredImages = activeFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeFilter);

  return (
    <div data-testid="gallery-page" className="min-h-screen pt-28 pb-20 bg-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Our Work</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1a365d] mb-4 font-bold">
            RC Awards Gallery
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Take a virtual tour of our San Diego showroom and see examples of our custom trophies, crystal awards, plaques, and more.
          </p>
        </motion.div>
      </section>

      {/* Filter Tabs */}
      <section className="max-w-6xl mx-auto px-6 mb-12">
        <div className="flex flex-wrap justify-center gap-2">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-[#1a365d] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedImage(image)}
              className="group cursor-pointer relative rounded-2xl overflow-hidden aspect-square shadow-lg"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                  <p className="text-white/70 text-sm">{image.alt}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Visit CTA */}
      <section className="bg-[#1a365d] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl text-white mb-4 font-bold">See Our Products in Person</h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto">
              Visit our San Diego showroom at 2509 El Cajon Blvd to browse our full selection of trophies, awards, and promotional products.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://maps.google.com/maps?q=2509+El+Cajon+Blvd,+San+Diego,+CA+92104"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center justify-center gap-2"
              >
                Get Directions
                <ArrowUpRight size={14} />
              </a>
              <Link
                to="/contact"
                className="border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              >
                Contact Us
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full p-2"
          >
            <X size={24} />
          </button>
          <motion.img
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-4 right-4 text-center">
            <h3 className="text-white font-semibold text-lg">{selectedImage.title}</h3>
            <p className="text-white/70 text-sm">{selectedImage.alt}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default GalleryPage;
